import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { batchesAPI } from '../../services/api';
import { normalizeApiListResponse } from '../../utils/apiListNormalize';
import TrainerStats from './TrainerStats';

/**
 * Trainer home: scoped batch list + student counts (presentation + light fetch).
 */
const TrainerBatchWorkspace = ({ stats }) => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const res = await batchesAPI.getAll();
        if (!cancelled) {
          setBatches(normalizeApiListResponse(res?.data));
        }
      } catch (err) {
        if (!cancelled) {
          setError(err?.response?.data?.message || 'Could not load your batches');
          setBatches([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Prefer counts from linked batches so the cards match the list below
  const displayStats = useMemo(() => {
    if (loading) return stats || {};
    const courseIds = new Set(
      batches
        .map((b) => b.courseId ?? b.course?.id)
        .filter((id) => id != null)
    );
    const studentIds = new Set();
    batches.forEach((b) => {
      (b.enrollments || []).forEach((e) => {
        const sid = e.studentId || e.student?.id;
        if (sid) studentIds.add(sid);
      });
    });
    return {
      batches: batches.length,
      assignedBatches: batches.length,
      courses: courseIds.size,
      students: studentIds.size || batches.reduce((n, b) => n + (b.enrollments?.length || 0), 0),
    };
  }, [loading, batches, stats]);

  return (
    <div className="staff-section">
      <div className="staff-section-head">
        <h3>Your batches</h3>
      </div>
      <TrainerStats stats={displayStats} />

      {error ? (
        <p className="staff-page-lead" style={{ color: '#b91c1c' }} role="alert">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="staff-page-lead">Loading your linked batches…</p>
      ) : batches.length === 0 ? (
        <p className="staff-page-lead">
          No batches are linked to your account yet. Ask an admin to open{' '}
          <strong>Batch management → Manage trainers</strong> and link you. After that you will only
          see students in those batches.
        </p>
      ) : (
        <>
          <p className="staff-page-lead">
            You only see students enrolled in the batches below. Use Filter students for search, or
            open a batch for the full roster.
          </p>
          <div className="staff-nav-grid">
            {batches.map((batch) => {
              const studentCount = batch.enrollments?.length ?? 0;
              return (
                <button
                  key={batch.id}
                  type="button"
                  className="staff-nav-tile"
                  onClick={() => navigate(`/admin/batches/${batch.id}`)}
                >
                  <span className="staff-nav-tile-title">{batch.batchName}</span>
                  <span className="staff-nav-tile-desc">
                    {batch.course?.courseName || 'Course'}
                    {batch.status ? ` · ${batch.status}` : ''}
                    {` · ${studentCount} student${studentCount === 1 ? '' : 's'}`}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <button
              type="button"
              className="theme-button-primary px-4 py-2 rounded-lg text-sm font-semibold"
              onClick={() => navigate('/admin/module-completion')}
            >
              Module completion
            </button>
            <button
              type="button"
              className="theme-button-secondary px-4 py-2 rounded-lg text-sm font-semibold"
              onClick={() => navigate('/admin/filter-students')}
            >
              Filter my students
            </button>
            <button
              type="button"
              className="theme-button-secondary px-4 py-2 rounded-lg text-sm font-semibold"
              onClick={() => navigate('/admin/batches')}
            >
              All my batches
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default TrainerBatchWorkspace;
